import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import Input from 'components/common/Input';
import SelectButton from 'components/common/SelectButton';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useForm from 'hooks/common/useForm';
import { luxonClone } from 'utils/time';
import * as Luxon from 'luxon';
import Circle from 'components/common/Circle';
import ExerciseDialogChart from 'components/charts/ExerciseDialogChart';
import CpfRatioBox from 'components/pages/Report/ReportDiet/CpfRatioBox';
import ExerciseDialogExcerciseTable from './ExerciseDialogExcerciseTable';
import ExerciseDialogDietTable from './ExerciseDialogDietTable';
import ExerciseDialogBloodSugarTable from './ExerciseDialogBloodSugarTable';
import ExerciseDialogInsulinTable from './ExerciseDialogInsulinTable';
import usePopupActive from 'hooks/communication/usePopupActive';
import ExerciseDialogGlocoseChart from 'components/charts/ExerciseDialogGlocoseChart';
import ExerciseDialogStepsChart from 'components/charts/ExerciseDialogStepsChart';
import WEEK_DAY_COLUMNS from 'constants/WEEK_DAY_COLUMNS';
import useReportMealDetail from 'hooks/communication/useReportMealDetail';
import CHECK_COLORS from 'constants/CHECK_COLORS';
import useRect from 'hooks/common/useRect';
import useImage from 'hooks/common/useImage';

const emtpyFoodList = [];

export default function DetailMealDialog(props) {
  const {
    index,
    count,
    onPrev,
    onNext,
    events,
    luxon = Luxon.DateTime.now(),
    ...rest
  } = props;

  const {
    requestReportMealDetail,
  } = useReportMealDetail();

  const meal_time = React.useMemo(() => luxon.toFormat('yyyyMMddHHmmss'), [luxon]);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const [data, setData] = React.useState({ uninitialized: true });
  React.useEffect(() => {
    requestReportMealDetail({
      patient_no,
      meal_time,
    }, (success, data) => {
      if (success) {
        setData(() => data);
      } else {
        setData(() => ({}));
      }
    })
  }, [meal_time, patient_no, requestReportMealDetail, setData]);

  const [date, weekday] = React.useMemo(() => [luxon.toFormat('yy.MM.dd'), luxonClone(luxon).weekday], [luxon]);

  const {
    meal_image_url,
    food_list = emtpyFoodList,
    beg_meal_time,
    beg_meal_bld_sugr,
    highest_time,
    highest_bld_sugr,
    tot_carbo,
    tot_protein,
    tot_fat,
    tot_cal,
    bldsugr,
    max_bldsugr,
    meal_bolus,
    correction_bolus,
    tot_bolus,
  } = data || {};

  const imgRef = React.useRef();
  const canvasRef = React.useRef();
  const rect = useRect(imgRef);

  React.useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    food_list.map((food, index) => {
      const {
        food_name,
        x1,
        y1,
        x2,
        y2,
        serving,
        max_height = 1440,
        max_width = 2560,
      } = food || {};

      const rectX = x1 / max_width * canvasRef.current.width;
      const rectY = y1 / max_height * canvasRef.current.height;
      const rectWidth = (x2 - x1) / max_width * canvasRef.current.width;
      const rectHeight = (y2 - y1) / max_height * canvasRef.current.height;

      ctx.strokeStyle = '#ffffff';
      // 사각형 그리기
      ctx.beginPath();
      ctx.roundRect(rectX, rectY, rectWidth, rectHeight, 10);
      ctx.stroke(); // 윤곽선 그리기 (fill을 사용하면 채워집니다)
      ctx.fillStyle = '#000000';
      ctx.globalAlpha = 0.5;
      ctx.fill();

      // 텍스트 속성 설정: 폰트, 중앙 정렬
      ctx.font = "16px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // 사각형의 중앙 좌표 계산
      const centerX = rectX + rectWidth / 2;
      const centerY = rectY + rectHeight / 2;

      // 텍스트 표시
      ctx.fillStyle = '#ffffff';
      ctx.fillText(food_name, centerX, centerY);

    });
  }, [food_list, rect]);

  const url = useImage(meal_image_url);
  if (data.uninitialized) return null;
  const beg_time = Luxon.DateTime.fromFormat(beg_meal_time, 'yyyyMMddHHmmss').toFormat('h:mm a').replace('PM', T('오후')).replace('AM', T('오전'));
  const high_time = Luxon.DateTime.fromFormat(highest_time, 'yyyyMMddHHmmss').toFormat('h:mm a').replace('PM', T('오후')).replace('AM', T('오전'));
  const day_time = luxon.toFormat('h:mm a').replace('PM', T('오후')).replace('AM', T('오전'));
  return (
    <VBox
      {...ex(['Dialog', 'DetailMealDialog'], rest)}
    >
      <HBox {...ex('DialogTitle')}>
        <HBox>
          {T('{0} ({1}) {2}', date, WEEK_DAY_COLUMNS[weekday - 1], day_time)}
        </HBox>
        <div>
          <FocusableButton
            {...{
              onClick: events?.onClose,
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.29279 6.29308C6.48031 6.10561 6.73462 6.00029 6.99979 6.00029C7.26495 6.00029 7.51926 6.10561 7.70679 6.29308L11.9998 10.5861L16.2928 6.29308C16.385 6.19757 16.4954 6.12139 16.6174 6.06898C16.7394 6.01657 16.8706 5.98898 17.0034 5.98783C17.1362 5.98668 17.2678 6.01198 17.3907 6.06226C17.5136 6.11254 17.6253 6.18679 17.7192 6.28069C17.8131 6.37458 17.8873 6.48623 17.9376 6.60913C17.9879 6.73202 18.0132 6.8637 18.012 6.99648C18.0109 7.12926 17.9833 7.26048 17.9309 7.38249C17.8785 7.50449 17.8023 7.61483 17.7068 7.70708L13.4138 12.0001L17.7068 16.2931C17.8889 16.4817 17.9897 16.7343 17.9875 16.9965C17.9852 17.2587 17.88 17.5095 17.6946 17.6949C17.5092 17.8803 17.2584 17.9855 16.9962 17.9878C16.734 17.99 16.4814 17.8892 16.2928 17.7071L11.9998 13.4141L7.70679 17.7071C7.51818 17.8892 7.26558 17.99 7.00339 17.9878C6.74119 17.9855 6.49038 17.8803 6.30497 17.6949C6.11956 17.5095 6.01439 17.2587 6.01211 16.9965C6.00983 16.7343 6.11063 16.4817 6.29279 16.2931L10.5858 12.0001L6.29279 7.70708C6.10532 7.51955 6 7.26525 6 7.00008C6 6.73492 6.10532 6.48061 6.29279 6.29308Z" fill="#5B6871" />
            </svg>
          </FocusableButton>
        </div>
      </HBox>
      <HBox full>
        <VBox full>
          <HBox full>
            {url && (
              <>
                <img src={url} alt={'meal'} ref={imgRef} />
                <canvas ref={canvasRef} width={rect.width} height={rect.height} />
              </>
            )}
          </HBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('혈당')}
            </HBox>
            <table>
              <tbody>
                <tr>
                  <th>
                    <div>
                      {T('식전 혈당')}
                    </div>
                  </th>
                  <td>
                    <HBox center>
                      <div {...ex(['BASE_COLOR', CHECK_COLORS('B', beg_meal_bld_sugr)])}>
                        {T('{0} ', beg_meal_bld_sugr)}
                      </div>
                      <div>
                        {T('mg/dL ({0})', beg_time)}
                      </div>
                    </HBox>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div>
                      {T('최고 혈당')}
                    </div>
                  </th>
                  <td>
                    <HBox center>
                      <div {...ex(['BASE_COLOR', CHECK_COLORS('B', highest_bld_sugr)])}>
                        {T('{0} ', highest_bld_sugr)}
                      </div>
                      <div>
                        {T('mg/dL ({0})', high_time)}
                      </div>
                    </HBox>
                  </td>
                </tr>
              </tbody>
            </table>
          </VBox>
        </VBox>
        <VBox full>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('음식')}
            </HBox>
            <HBox full {...ex('FoodTable')}>
              <table >
                <thead>
                  <tr>
                    <th><div>{T('이름')}</div></th>
                    <th><div>{T('탄수화물(g)')}</div></th>
                    <th><div>{T('단백질(g)')}</div></th>
                    <th><div>{T('지방(g)')}</div></th>
                    <th><div>{T('칼로리(kcal)')}</div></th>
                    <th><div>{T('섭취량(g)')}</div></th>
                  </tr>
                </thead>
                <tbody>
                  {food_list.map((food, index) => {
                    const {
                      food_name,
                      carbo,
                      protein,
                      fat,
                      food_calorie_amt,
                      food_amt,
                    } = food || {}
                    return (
                      <tr key={index}>
                        <td><div>{food_name}</div></td>
                        <td><div>{carbo}</div></td>
                        <td><div>{protein}</div></td>
                        <td><div>{fat}</div></td>
                        <td><div>{food_calorie_amt}</div></td>
                        <td><div>{food_amt}</div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </HBox>
            <hr />
            <table>
              <tbody>
                <tr>
                  <th>
                    <div>
                      {T('총 칼로리')}
                    </div>
                  </th>
                  <td>
                    <div>
                      {T('{0} kcal', tot_cal)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div>
                      {T('총 탄단지')}
                    </div>
                  </th>
                  <td>
                    <div>
                      {T('{0}-{1}-{2}', tot_carbo, tot_protein, tot_fat)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </VBox>

          <VBox>
            <HBox {...ex('Caption')}>
              {T('인슐린')}
            </HBox>
            <table>
              <tbody>
                <tr>
                  <th>
                    <div>
                      {T('권장 식사 볼루스')}
                    </div>
                  </th>
                  <td>
                    <div>
                      {T('{0} U', meal_bolus)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div>
                      {T('권장 교정 볼루스')}
                    </div>
                  </th>
                  <td>
                    <div>
                      {T('{0} U', correction_bolus)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div>
                      {T('권장 총 볼루스')}
                    </div>
                  </th>
                  <td>
                    <div>
                      {T('{0} U', tot_bolus)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </VBox>
        </VBox>
      </HBox>
    </VBox >
  );
}
